import React, { useState, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * LoginBrandingManager - Interface d'administration pour personnaliser la page de connexion
 * Permet de modifier le fond, le logo, les couleurs et le style de la carte de connexion
 */
const LoginBrandingManager = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    background_type: 'color',
    background_image_url: null,
    background_color: '#f0f9f6',
    gradient_from: '#1db584',
    gradient_to: '#16a375',
    gradient_direction: '135deg',
    overlay_enabled: false,
    overlay_color: '#000000',
    overlay_opacity: 0.3,
    logo_url: null,
    logo_alt: 'Baobab Connect',
    logo_position: 'top-center',
    logo_max_width: 140,
    primary_color: '#1db584',
    card_style: 'solid',
    card_background: '#ffffff',
    card_blur: 10,
    title_text: 'Baobab Connect',
    subtitle_text: 'ERP / CRM Interne',
    welcome_text: 'Connectez-vous à votre espace',
    tagline_text: "L'Afrique avance, nous connectons.",
    tagline_enabled: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const bgInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/login-branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/login-branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
        setMessage({ type: 'success', text: 'Paramètres enregistrés avec succès !' });
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde des paramètres' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser aux valeurs par défaut ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/login-branding/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data.settings }));
        setMessage({ type: 'success', text: 'Paramètres réinitialisés avec succès !' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la réinitialisation' });
    }
  };

  const handleImageUpload = async (file, type) => {
    const setUploading = type === 'background' ? setUploadingBg : setUploadingLogo;
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/settings/login-branding/upload-image?image_type=${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fieldName = type === 'background' ? 'background_image_url' : 'logo_url';
        setSettings(prev => ({ ...prev, [fieldName]: data.url }));
        setMessage({ type: 'success', text: `Image ${type === 'background' ? 'de fond' : 'logo'} téléchargée !` });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur upload');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Générer le style de prévisualisation
  const getPreviewStyle = () => {
    let style = { minHeight: '400px', position: 'relative' };
    
    if (!settings.enabled) {
      style.backgroundColor = '#f0f9f6';
      return style;
    }
    
    switch (settings.background_type) {
      case 'image':
        if (settings.background_image_url) {
          style.backgroundImage = `url(${settings.background_image_url})`;
          style.backgroundSize = 'cover';
          style.backgroundPosition = 'center';
        } else {
          style.backgroundColor = settings.background_color;
        }
        break;
      case 'gradient':
        style.background = `linear-gradient(${settings.gradient_direction}, ${settings.gradient_from}, ${settings.gradient_to})`;
        break;
      default:
        style.backgroundColor = settings.background_color;
    }
    
    return style;
  };

  const getCardStyle = () => {
    let style = {
      backgroundColor: settings.card_background,
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '400px',
      width: '100%',
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
    };
    
    if (settings.enabled && settings.card_style === 'glass') {
      style.backgroundColor = `${settings.card_background}cc`;
      style.backdropFilter = `blur(${settings.card_blur}px)`;
      style.border = '1px solid rgba(255,255,255,0.2)';
    }
    
    return style;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <span className="bg-white/20 p-2 rounded-lg">🎨</span>
              Personnalisation Page de Connexion
            </h2>
            <p className="text-purple-100 mt-1">
              Personnalisez l'apparence de votre page de connexion
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-semibold disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      {/* Message de notification */}
      {message.text && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panneau de configuration */}
        <div className="space-y-6">
          {/* Activation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Activer la personnalisation</h3>
                <p className="text-sm text-gray-500">Activez pour appliquer vos personnalisations</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => updateSetting('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </div>

          {/* Type de fond */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🖼️ Arrière-plan</h3>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                {[
                  { id: 'color', label: 'Couleur', icon: '🎨' },
                  { id: 'gradient', label: 'Dégradé', icon: '🌈' },
                  { id: 'image', label: 'Image', icon: '🖼️' }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => updateSetting('background_type', type.id)}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      settings.background_type === type.id
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>

              {settings.background_type === 'color' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Couleur de fond</label>
                  <div className="flex gap-3 items-center">
                    <input
                      type="color"
                      value={settings.background_color}
                      onChange={(e) => updateSetting('background_color', e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.background_color}
                      onChange={(e) => updateSetting('background_color', e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              )}

              {settings.background_type === 'gradient' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Couleur de départ</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={settings.gradient_from}
                          onChange={(e) => updateSetting('gradient_from', e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.gradient_from}
                          onChange={(e) => updateSetting('gradient_from', e.target.value)}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Couleur de fin</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={settings.gradient_to}
                          onChange={(e) => updateSetting('gradient_to', e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.gradient_to}
                          onChange={(e) => updateSetting('gradient_to', e.target.value)}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
                    <select
                      value={settings.gradient_direction}
                      onChange={(e) => updateSetting('gradient_direction', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="0deg">↑ Haut</option>
                      <option value="45deg">↗ Diagonal haut-droite</option>
                      <option value="90deg">→ Droite</option>
                      <option value="135deg">↘ Diagonal bas-droite</option>
                      <option value="180deg">↓ Bas</option>
                      <option value="225deg">↙ Diagonal bas-gauche</option>
                      <option value="270deg">← Gauche</option>
                      <option value="315deg">↖ Diagonal haut-gauche</option>
                    </select>
                  </div>
                </div>
              )}

              {settings.background_type === 'image' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image de fond</label>
                    <input
                      type="file"
                      ref={bgInputRef}
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'background')}
                      className="hidden"
                    />
                    <button
                      onClick={() => bgInputRef.current?.click()}
                      disabled={uploadingBg}
                      className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all text-gray-600"
                    >
                      {uploadingBg ? 'Téléchargement...' : settings.background_image_url ? '📷 Changer l\'image' : '📷 Télécharger une image'}
                    </button>
                    {settings.background_image_url && (
                      <p className="text-xs text-green-600 mt-2">✓ Image de fond configurée</p>
                    )}
                  </div>
                  
                  {/* Overlay */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Overlay (voile)</span>
                      <input
                        type="checkbox"
                        checked={settings.overlay_enabled}
                        onChange={(e) => updateSetting('overlay_enabled', e.target.checked)}
                        className="w-5 h-5 text-indigo-600 rounded"
                      />
                    </div>
                    {settings.overlay_enabled && (
                      <div className="space-y-3">
                        <div className="flex gap-3 items-center">
                          <input
                            type="color"
                            value={settings.overlay_color}
                            onChange={(e) => updateSetting('overlay_color', e.target.value)}
                            className="w-12 h-8 rounded cursor-pointer"
                          />
                          <span className="text-sm text-gray-600">Couleur de l'overlay</span>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Opacité: {Math.round(settings.overlay_opacity * 100)}%</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={settings.overlay_opacity}
                            onChange={(e) => updateSetting('overlay_opacity', parseFloat(e.target.value))}
                            className="w-full"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Logo */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🏷️ Logo</h3>
            
            <div className="space-y-4">
              <div>
                <input
                  type="file"
                  ref={logoInputRef}
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'logo')}
                  className="hidden"
                />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all text-gray-600"
                >
                  {uploadingLogo ? 'Téléchargement...' : settings.logo_url ? '🏷️ Changer le logo' : '🏷️ Télécharger un logo'}
                </button>
                {settings.logo_url && (
                  <div className="mt-3 flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                    <img src={settings.logo_url} alt="Logo" className="max-h-16 object-contain" />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                  <select
                    value={settings.logo_position}
                    onChange={(e) => updateSetting('logo_position', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="top-left">Haut gauche</option>
                    <option value="top-center">Haut centre</option>
                    <option value="top-right">Haut droite</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Largeur max (px)</label>
                  <input
                    type="number"
                    value={settings.logo_max_width}
                    onChange={(e) => updateSetting('logo_max_width', parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="50"
                    max="300"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Style de la carte et couleurs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🎯 Style & Couleurs</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Couleur principale (bouton)</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => updateSetting('primary_color', e.target.value)}
                    className="w-16 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => updateSetting('primary_color', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Style de la carte</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateSetting('card_style', 'solid')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      settings.card_style === 'solid'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Solide
                  </button>
                  <button
                    onClick={() => updateSetting('card_style', 'glass')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      settings.card_style === 'glass'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Glass (Verre)
                  </button>
                </div>
              </div>

              {settings.card_style === 'glass' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Flou (blur): {settings.card_blur}px</label>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    value={settings.card_blur}
                    onChange={(e) => updateSetting('card_blur', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Textes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📝 Textes</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre principal</label>
                <input
                  type="text"
                  value={settings.title_text}
                  onChange={(e) => updateSetting('title_text', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                <input
                  type="text"
                  value={settings.subtitle_text}
                  onChange={(e) => updateSetting('subtitle_text', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message d'accueil</label>
                <input
                  type="text"
                  value={settings.welcome_text}
                  onChange={(e) => updateSetting('welcome_text', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings.tagline_enabled}
                  onChange={(e) => updateSetting('tagline_enabled', e.target.checked)}
                  className="w-5 h-5 text-indigo-600 rounded"
                />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                  <input
                    type="text"
                    value={settings.tagline_text}
                    onChange={(e) => updateSetting('tagline_text', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={!settings.tagline_enabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Panneau de prévisualisation */}
        <div className="lg:sticky lg:top-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span>👁️</span>
                Aperçu en direct
              </h3>
            </div>
            
            <div 
              className="flex items-center justify-center"
              style={getPreviewStyle()}
            >
              {/* Overlay */}
              {settings.enabled && settings.background_type === 'image' && settings.overlay_enabled && (
                <div 
                  className="absolute inset-0"
                  style={{ backgroundColor: settings.overlay_color, opacity: settings.overlay_opacity }}
                />
              )}
              
              {/* Carte de connexion */}
              <div 
                className="relative z-10 m-8"
                style={getCardStyle()}
              >
                {/* Logo */}
                {settings.enabled && settings.logo_url && (
                  <div className={`mb-4 ${
                    settings.logo_position === 'top-left' ? 'text-left' :
                    settings.logo_position === 'top-right' ? 'text-right' : 'text-center'
                  }`}>
                    <img 
                      src={settings.logo_url} 
                      alt={settings.logo_alt}
                      style={{ maxWidth: `${settings.logo_max_width}px`, display: 'inline-block' }}
                    />
                  </div>
                )}
                
                {/* Contenu */}
                <div className="text-center mb-6">
                  <h2 
                    className="text-2xl font-bold mb-2"
                    style={{ color: settings.primary_color }}
                  >
                    {settings.title_text}
                  </h2>
                  <h3 className="text-xl font-semibold text-gray-900">{settings.subtitle_text}</h3>
                  <p className="text-gray-600 mt-2">{settings.welcome_text}</p>
                </div>

                {/* Formulaire simulé */}
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Nom d'utilisateur</div>
                    <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-400">
                      demo@example.com
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Mot de passe</div>
                    <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-400">
                      ••••••••
                    </div>
                  </div>
                  <button
                    className="w-full py-3 px-4 rounded-lg text-white font-semibold transition-colors"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    Se connecter
                  </button>
                </div>

                {/* Tagline */}
                {settings.tagline_enabled && (
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500 italic">
                      "{settings.tagline_text}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginBrandingManager;
